import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import AccountTreeRoundedIcon from "@mui/icons-material/AccountTreeRounded";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import QuizRoundedIcon from "@mui/icons-material/QuizRounded";
import StyleRoundedIcon from "@mui/icons-material/StyleRounded";
import RocketLaunchRoundedIcon from "@mui/icons-material/RocketLaunchRounded";
import RestartAltRoundedIcon from "@mui/icons-material/RestartAltRounded";
import PanToolAltRoundedIcon from "@mui/icons-material/PanToolAltRounded";
import HubRoundedIcon from "@mui/icons-material/HubRounded";
import FiberManualRecordRoundedIcon from "@mui/icons-material/FiberManualRecordRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import RemoveRoundedIcon from "@mui/icons-material/RemoveRounded";
import CenterFocusStrongRoundedIcon from "@mui/icons-material/CenterFocusStrongRounded";
import MDButton from "components/MDButton";
import {
  STATIC_COPILOT_COSTS,
  sendCopilotChatCall,
} from "../../../redux/api/copilot.api";

/*
  TopicsTab — top-down tree of topic cards
  ──────────────────────────────────────────────────────────────
  Renders studyPack.studyChart as a vertical tree: the lecture's
  root concept sits at the top, immediate sub-topics fan out
  below it, leaves at the bottom. Each node is a rounded card
  (not a circle) so it can carry a title + a small coverage row
  ("3 quiz · 5 cards") that makes the map functional, not just
  decorative.

  Why a tree instead of a radial graph?
    • Radial layouts read as "everything radiates from one
      concept", which is misleading when the chart actually has
      depth (root → broad topic → sub-topic → leaf).
    • A tree puts the natural reading order (top→bottom) in
      front of the user and shows hierarchy at a glance.
    • Edges fan out instead of crossing — no more "where does
      this line go?" complaint.

  Architecture
  ────────────
  Two stacked layers inside one positioned container:
    1. <svg> — absolutely positioned, paints the curved edges
       beneath the cards. Edges read parent (top) to child
       (bottom) so the arrowhead always points down.
    2. HTML cards — one per node, positioned absolutely with
       transform: translate(-50%, -50%) so x/y refer to the
       card's centre. This lets us use the full MUI styling
       toolkit (gradients, shadows, hover) without recreating
       any of it in SVG.

  The whole stack lives in a scrollable parent so wide trees
  scroll horizontally and tall trees scroll vertically without
  bleeding into the page layout.

  Interaction
  ───────────
  Cards are draggable. We track a per-id position override
  (`positions`); the tree layout is reapplied whenever the
  underlying chart changes. Click vs. drag is disambiguated by
  total pointer travel (under 4 px = click → drill dialog).
*/

const CARD_W = 188;
const CARD_H = 64;
const LEVEL_GAP_Y = 110;
const SIBLING_GAP_X = 26;
const TOP_PAD = 36;
const SIDE_PAD = 48;
const CLICK_DRAG_THRESHOLD = 4;

const MIN_ZOOM = 0.4;
const MAX_ZOOM = 2;
const ZOOM_STEP = 0.15;

/**
 * Build a multi-level hierarchy from a flat node/edge graph.
 *
 * The Copilot's studyChart generator produces a dense relationship
 * graph rather than a tree — every topic is connected to every
 * related topic with directional edges. A naive BFS-from-root over
 * that ends up flat (one ring of leaves around the root), which is
 * what the user complained about: "all the leaves with different
 * hierarchy should go under the parent leaf".
 *
 * Strategy:
 *   1. Pick a root: an explicit `isRoot` flag wins, otherwise the
 *      highest-degree node (it's almost always the central
 *      concept the AI built the rest of the chart around).
 *   2. Run BFS from root over the undirected edges to capture the
 *      NATURAL hierarchy. If that produces depth >= 2, use it as
 *      is — the AI already did a good job.
 *   3. Otherwise, the graph is near-star-shaped. Synthesize a
 *      depth-2 tree:
 *        - Promote the top-K non-root nodes by degree (K scales
 *          with √(N-1), capped at 4) to be "hub" cards at level 1.
 *        - Bucket every remaining node into the hub that shares
 *          the strongest connection (a real edge first, then the
 *          same `group`, then round-robin as a last resort).
 *
 * The returned `children` map drives the layout positioning pass.
 */
function synthesizeHierarchy(nodes, edges) {
  if (!nodes.length) return { rootId: null, children: new Map() };

  const adj = new Map();
  const degree = new Map();
  for (const n of nodes) {
    adj.set(n.id, new Set());
    degree.set(n.id, 0);
  }
  for (const e of edges) {
    const f = e.source || e.from;
    const t = e.target || e.to;
    if (!adj.has(f) || !adj.has(t)) continue;
    if (!adj.get(f).has(t)) {
      adj.get(f).add(t);
      adj.get(t).add(f);
      degree.set(f, (degree.get(f) || 0) + 1);
      degree.set(t, (degree.get(t) || 0) + 1);
    }
  }

  // 1) Root selection.
  let root = nodes.find((n) => n.isRoot || n.is_root);
  if (!root) {
    root = nodes.reduce(
      (best, n) =>
        (degree.get(n.id) || 0) > (degree.get(best?.id) || -1) ? n : best,
      nodes[0],
    );
  }

  // 2) Natural BFS (undirected) — captures the AI's own
  //    hierarchy when it is genuinely tree-shaped.
  const naturalChildren = new Map();
  const visited = new Set([root.id]);
  const levels = new Map([[root.id, 0]]);
  const queue = [root.id];
  let maxLevel = 0;
  while (queue.length) {
    const cur = queue.shift();
    const neighbors = Array.from(adj.get(cur) || []);
    // Stable order by degree (highest first) so the most
    // connected neighbours become parents of bigger subtrees.
    neighbors.sort(
      (a, b) => (degree.get(b) || 0) - (degree.get(a) || 0),
    );
    for (const n of neighbors) {
      if (visited.has(n)) continue;
      visited.add(n);
      const lv = (levels.get(cur) || 0) + 1;
      levels.set(n, lv);
      maxLevel = Math.max(maxLevel, lv);
      if (!naturalChildren.has(cur)) naturalChildren.set(cur, []);
      naturalChildren.get(cur).push(n);
    }
    // BFS over the WHOLE frontier first so we don't accidentally
    // turn what should be siblings into a parent-child chain.
    for (const n of neighbors) {
      if (levels.get(n) === (levels.get(cur) || 0) + 1) queue.push(n);
    }
  }
  // Orphans: hang off root at level 1.
  for (const n of nodes) {
    if (!visited.has(n.id)) {
      if (!naturalChildren.has(root.id)) naturalChildren.set(root.id, []);
      naturalChildren.get(root.id).push(n.id);
      levels.set(n.id, 1);
      visited.add(n.id);
      maxLevel = Math.max(maxLevel, 1);
    }
  }

  if (maxLevel >= 2) {
    return { rootId: root.id, children: naturalChildren };
  }

  // 3) Synthesize depth-2: hubs by degree, leaves bucketed.
  const others = nodes.filter((n) => n.id !== root.id);
  const numHubs = Math.min(
    4,
    Math.max(2, Math.round(Math.sqrt(Math.max(1, others.length)))),
  );
  const sorted = [...others].sort(
    (a, b) => (degree.get(b.id) || 0) - (degree.get(a.id) || 0),
  );
  const hubs = sorted.slice(0, numHubs);
  const leaves = sorted.slice(numHubs);
  const hubIdSet = new Set(hubs.map((h) => h.id));

  const synthChildren = new Map();
  synthChildren.set(
    root.id,
    hubs.map((h) => h.id),
  );
  for (const h of hubs) synthChildren.set(h.id, []);

  // Pre-sort hubs by current load so round-robin biases toward
  // the emptiest hub at any moment — keeps the tree balanced.
  const hubLoad = new Map(hubs.map((h) => [h.id, 0]));
  const pickEmptiestHub = () => {
    let bestId = hubs[0].id;
    let bestLoad = Infinity;
    for (const h of hubs) {
      const l = hubLoad.get(h.id) || 0;
      if (l < bestLoad) {
        bestLoad = l;
        bestId = h.id;
      }
    }
    return bestId;
  };

  for (const leaf of leaves) {
    let assignedHub = null;
    // (a) Shared edge with a hub.
    const neighbors = adj.get(leaf.id) || new Set();
    for (const h of hubs) {
      if (neighbors.has(h.id)) {
        assignedHub = h.id;
        break;
      }
    }
    // (b) Same `group` as a hub.
    if (!assignedHub && leaf.group !== undefined) {
      const matched = hubs.find(
        (h) => h.group !== undefined && h.group === leaf.group,
      );
      if (matched) assignedHub = matched.id;
    }
    // (c) Pick the emptiest hub.
    if (!assignedHub) assignedHub = pickEmptiestHub();

    synthChildren.get(assignedHub).push(leaf.id);
    hubLoad.set(assignedHub, (hubLoad.get(assignedHub) || 0) + 1);
  }

  // Ensure every hub has a children entry (even empty).
  return { rootId: root.id, children: synthChildren };
}

/**
 * Build a strict tree from the chart's nodes + edges, then
 * compute every node's centre (x, y) using a subtree-width
 * packing algorithm:
 *
 *   1. Run `synthesizeHierarchy` to turn the chart's general
 *      graph into a multi-level tree (with hub-bucketing when
 *      the natural BFS would have been too flat).
 *   2. Recursively compute each node's subtree width as the
 *      sum of its children's widths plus inter-sibling gaps.
 *   3. Recursively position each node centred over its
 *      children's combined width. Parents always sit directly
 *      above the midpoint of their children → no edge
 *      crossings inside a single subtree.
 */
function buildTreeLayout(chart) {
  const empty = {
    nodes: [],
    edges: [],
    bounds: { w: 600, h: 360 },
    cardW: CARD_W,
    cardH: CARD_H,
  };
  if (!chart || !Array.isArray(chart.nodes) || chart.nodes.length === 0) {
    return empty;
  }

  // Hierarchy: parent → ordered children, root id.
  const { rootId, children: tree } = synthesizeHierarchy(
    chart.nodes,
    chart.edges || [],
  );
  if (!rootId) return empty;

  const root = chart.nodes.find((n) => n.id === rootId) || chart.nodes[0];

  // Levels are derived from the hierarchy structure (not the
  // raw edges) so the layout reflects what the user will see.
  const levels = new Map([[root.id, 0]]);
  const positionStack = [root.id];
  while (positionStack.length) {
    const cur = positionStack.pop();
    const kids = tree.get(cur) || [];
    const lv = (levels.get(cur) || 0) + 1;
    for (const k of kids) {
      if (!levels.has(k)) levels.set(k, lv);
      positionStack.push(k);
    }
  }
  // Ensure every node has a level entry; orphans pinned to root.
  for (const n of chart.nodes) {
    if (!levels.has(n.id)) levels.set(n.id, 1);
  }
  // Ensure `tree` has an entry for every node id we'll touch
  // during the position pass.
  for (const n of chart.nodes) {
    if (!tree.has(n.id)) tree.set(n.id, []);
  }

  // Subtree width (leaves bottom-up).
  const subtreeW = new Map();
  function computeWidth(id) {
    const kids = tree.get(id) || [];
    if (kids.length === 0) {
      subtreeW.set(id, CARD_W);
      return CARD_W;
    }
    let sum = 0;
    for (const k of kids) sum += computeWidth(k);
    sum += (kids.length - 1) * SIBLING_GAP_X;
    const w = Math.max(CARD_W, sum);
    subtreeW.set(id, w);
    return w;
  }
  computeWidth(root.id);

  // Position pass: place each node at the centre of its
  // subtree's horizontal slot.
  const positions = new Map();
  function positionNode(id, left, level) {
    const w = subtreeW.get(id) || CARD_W;
    const cx = left + w / 2;
    const cy = TOP_PAD + level * LEVEL_GAP_Y + CARD_H / 2;
    positions.set(id, { x: cx, y: cy });
    const kids = tree.get(id) || [];
    if (kids.length === 0) return;
    let cursor = left;
    // For multi-child layouts we want the children clustered
    // under the parent's centre, not the slot's left edge,
    // when the parent's slot is wider than the children's
    // combined width (which happens when CARD_W is wider than
    // a single-leaf subtree). Compute the children's actual
    // total width then centre them within the slot.
    let childrenTotalW = 0;
    for (const k of kids) childrenTotalW += subtreeW.get(k) || CARD_W;
    childrenTotalW += (kids.length - 1) * SIBLING_GAP_X;
    cursor = cx - childrenTotalW / 2;
    for (const k of kids) {
      positionNode(k, cursor, level + 1);
      cursor += (subtreeW.get(k) || CARD_W) + SIBLING_GAP_X;
    }
  }
  positionNode(root.id, SIDE_PAD, 0);

  // Canvas bounds: width = root subtree + side padding, height
  // = (max level + 1) levels worth of vertical gaps + bottom
  // padding for the card body.
  let maxLevel = 0;
  for (const lv of levels.values()) maxLevel = Math.max(maxLevel, lv);
  const canvasW = (subtreeW.get(root.id) || CARD_W) + SIDE_PAD * 2;
  const canvasH =
    TOP_PAD + (maxLevel + 1) * LEVEL_GAP_Y + (CARD_H / 2) + 32;

  const positioned = chart.nodes.map((n) => {
    const pos =
      positions.get(n.id) || {
        x: SIDE_PAD + CARD_W / 2,
        y: TOP_PAD + CARD_H / 2,
      };
    return {
      ...n,
      x: pos.x,
      y: pos.y,
      level: levels.get(n.id) || 0,
      isRoot: n.id === root.id,
    };
  });

  // Hierarchy edges: synthesised parent→child links derived
  // from the tree. These are what we want to DRAW (they
  // actually correspond to the visual layout). The chart's
  // raw semantic edges are intentionally NOT rendered as
  // lines — drawing them on top of a synthesised tree creates
  // criss-crossing arrows that make the chart unreadable; the
  // detail dialog on tap is the right place to expose them.
  const hierarchyEdges = [];
  for (const [parent, kids] of tree.entries()) {
    for (const kid of kids) {
      hierarchyEdges.push({ from: parent, to: kid });
    }
  }

  return {
    nodes: positioned,
    edges: hierarchyEdges,
    bounds: { w: canvasW, h: canvasH },
    cardW: CARD_W,
    cardH: CARD_H,
    rootId: root.id,
  };
}

/**
 * Coverage category — drives the card's gradient + label tint.
 */
function categoryOf(node) {
  if (node.isRoot) return "root";
  const coverage = (node.quizCount || 0) + (node.flashcardCount || 0);
  if (coverage >= 4) return "high";
  if (coverage >= 1) return "mid";
  return "none";
}

/**
 * Theme-aware gradient stops and label colour for a node card.
 * Picks a stop set from the active palette so light and dark
 * mode both look intentional without separate tuning.
 */
function cardPalette(node, theme) {
  const isDark = theme.palette.mode === "dark";
  switch (categoryOf(node)) {
    case "root":
      return {
        gradient: `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.dark} 100%)`,
        border: theme.palette.primary.main,
        text: theme.palette.primary.contrastText || "#fff",
        sub: alpha("#fff", 0.85),
      };
    case "high":
      return {
        gradient: `linear-gradient(135deg, ${theme.palette.success.light} 0%, ${theme.palette.success.dark} 100%)`,
        border: theme.palette.success.main,
        text: "#fff",
        sub: alpha("#fff", 0.88),
      };
    case "mid":
      return {
        gradient: `linear-gradient(135deg, ${theme.palette.info.light} 0%, ${theme.palette.info.dark} 100%)`,
        border: theme.palette.info.main,
        text: "#fff",
        sub: alpha("#fff", 0.88),
      };
    default:
      // "None / leaf" — uses a neutral surface card with a
      // coloured left edge so it doesn't compete with covered
      // topics for visual weight.
      return {
        gradient: isDark
          ? `linear-gradient(135deg, ${alpha("#0f172a", 0.92)} 0%, ${alpha(
              "#1e293b",
              0.92,
            )} 100%)`
          : `linear-gradient(135deg, ${alpha("#ffffff", 1)} 0%, ${alpha(
              "#f8fafc",
              1,
            )} 100%)`,
        border: alpha(theme.palette.warning.main, isDark ? 0.7 : 0.55),
        text: theme.palette.text.primary,
        sub: theme.palette.text.secondary,
      };
  }
}

export default function TopicsTab({
  materialId,
  studyPack,
  credits,
  onStudyPackUpdate,
  onCreditsChange,
  onInsufficientCredits,
  onNavigateToTab,
}) {
  const theme = useTheme();
  const chart = studyPack?.studyChart;
  const [drillNode, setDrillNode] = useState(null);
  const [deepening, setDeepening] = useState(false);

  // Per-id position overrides applied on top of the computed
  // tree layout. Reset whenever the chart identity changes
  // (e.g. after Deepen-with-AI), or when the user clicks the
  // Reset-layout button.
  const [positions, setPositions] = useState({});
  const [dragging, setDragging] = useState(null);
  const [hoverNodeId, setHoverNodeId] = useState(null);

  // Refs for client-coordinate ↔ canvas-coordinate conversion
  // and stable access from the window-level pointermove
  // listener installed during a drag.
  const canvasRef = useRef(null);
  const scrollWrapRef = useRef(null);
  const draggingRef = useRef(null);
  const zoomRef = useRef(1);
  // `autoFitDone` ensures we measure-and-fit the canvas only on
  // the FIRST render after the chart changes — after that the
  // user owns the zoom. Without this, every render that
  // changed the container size would yank the zoom back to fit.
  const autoFitDoneRef = useRef(false);

  const layout = useMemo(() => buildTreeLayout(chart), [chart]);

  // Zoom state. Default to 1; the auto-fit effect below replaces
  // it on mount with whatever value makes the whole tree visible.
  const [zoom, setZoom] = useState(1);
  zoomRef.current = zoom;
  const clampZoom = useCallback(
    (z) => Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, z)),
    [],
  );

  useEffect(() => {
    setPositions({});
    autoFitDoneRef.current = false;
  }, [chart]);

  /*
    Auto-fit on first layout: measure the scroll-wrapper's
    visible width and pick a zoom factor that makes the whole
    tree fit inside it. We never go ABOVE 1 (cards shouldn't
    blow up just because there's room) and we floor at
    MIN_ZOOM so labels stay legible on huge trees.
    Runs after `layout` becomes non-empty so the bounds are
    real.
  */
  useEffect(() => {
    if (autoFitDoneRef.current) return;
    if (!layout?.bounds?.w) return;
    const el = scrollWrapRef.current;
    if (!el) return;
    const containerW = el.clientWidth || 600;
    // 24 px of breathing room on each side so the tree never
    // feels jammed against the scrollbars.
    const target = clampZoom(
      Math.min(1, (containerW - 48) / layout.bounds.w),
    );
    setZoom(target);
    autoFitDoneRef.current = true;
  }, [layout, clampZoom]);

  /**
   * Convert client (CSS) coords to layout-space coords.
   * `canvasRef` points at the UNSCALED inner canvas box, but its
   * visual bounding rect is post-CSS-transform — so the rect's
   * width equals `layout.bounds.w * zoom`. We divide by the live
   * zoom factor to translate the visual offset back into the
   * layout coordinate system the cards and hit-test math use.
   */
  const toCanvasPoint = useCallback((clientX, clientY) => {
    const el = canvasRef.current;
    if (!el) return { x: clientX, y: clientY };
    const rect = el.getBoundingClientRect();
    const z = zoomRef.current || 1;
    return {
      x: (clientX - rect.left) / z,
      y: (clientY - rect.top) / z,
    };
  }, []);

  const zoomIn = useCallback(
    () => setZoom((z) => clampZoom(z + ZOOM_STEP)),
    [clampZoom],
  );
  const zoomOut = useCallback(
    () => setZoom((z) => clampZoom(z - ZOOM_STEP)),
    [clampZoom],
  );
  const zoomFit = useCallback(() => {
    if (!layout?.bounds?.w) {
      setZoom(1);
      return;
    }
    const el = scrollWrapRef.current;
    const containerW = el?.clientWidth || 600;
    setZoom(clampZoom(Math.min(1, (containerW - 48) / layout.bounds.w)));
  }, [layout, clampZoom]);

  const handleCardPointerDown = useCallback(
    (e, node) => {
      // Don't let the drag also start a text selection or
      // bubble to scrollable parents.
      e.preventDefault();
      e.stopPropagation();
      const pt = toCanvasPoint(e.clientX, e.clientY);
      const current = positions[node.id] || { x: node.x, y: node.y };
      const state = {
        nodeId: node.id,
        offsetX: current.x - pt.x,
        offsetY: current.y - pt.y,
        startClientX: e.clientX,
        startClientY: e.clientY,
        moved: 0,
      };
      setDragging(state);
      draggingRef.current = state;
    },
    [positions, toCanvasPoint],
  );

  useEffect(() => {
    if (!dragging) return undefined;
    const handleMove = (ev) => {
      const state = draggingRef.current;
      if (!state) return;
      const pt = toCanvasPoint(ev.clientX, ev.clientY);
      const dxScreen = ev.clientX - state.startClientX;
      const dyScreen = ev.clientY - state.startClientY;
      const moved = Math.sqrt(dxScreen * dxScreen + dyScreen * dyScreen);
      state.moved = Math.max(state.moved, moved);
      setPositions((prev) => ({
        ...prev,
        [state.nodeId]: {
          x: pt.x + state.offsetX,
          y: pt.y + state.offsetY,
        },
      }));
    };
    const handleUp = () => {
      const state = draggingRef.current;
      if (state && state.moved < CLICK_DRAG_THRESHOLD) {
        const node = layout.nodes.find((n) => n.id === state.nodeId);
        if (node) setDrillNode(node);
      }
      draggingRef.current = null;
      setDragging(null);
    };
    window.addEventListener("pointermove", handleMove);
    window.addEventListener("pointerup", handleUp);
    window.addEventListener("pointercancel", handleUp);
    return () => {
      window.removeEventListener("pointermove", handleMove);
      window.removeEventListener("pointerup", handleUp);
      window.removeEventListener("pointercancel", handleUp);
    };
  }, [dragging, layout.nodes, toCanvasPoint]);

  const resolvePos = useCallback(
    (node) => positions[node.id] || { x: node.x, y: node.y },
    [positions],
  );

  const composerCost =
    credits?.costs?.copilot_chat ?? STATIC_COPILOT_COSTS.copilot_chat;

  const deepenChart = async () => {
    if (deepening) return;
    if (credits && Number(credits.creditsRemaining) < composerCost) {
      onInsufficientCredits?.({
        required: composerCost,
        remaining: credits.creditsRemaining,
      });
      return;
    }
    setDeepening(true);
    try {
      const res = await sendCopilotChatCall({
        materialId,
        message: "",
        intent: "deepen_study_chart",
      });
      if (res?.status === 402) {
        onInsufficientCredits?.({
          required: res?.credits?.required ?? composerCost,
          remaining: res?.credits?.creditsRemaining ?? 0,
        });
        return;
      }
      if (res?.studyPack) onStudyPackUpdate?.(res.studyPack);
      if (res?.credits) onCreditsChange?.(res.credits);
    } finally {
      setDeepening(false);
    }
  };

  const resetLayout = () => setPositions({});

  const drillCoverage = drillNode
    ? (drillNode.quizCount || 0) + (drillNode.flashcardCount || 0)
    : 0;

  const hasPositionOverrides = Object.keys(positions).length > 0;

  return (
    <Stack spacing={2}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        spacing={1.5}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <AccountTreeRoundedIcon color="primary" />
          <Box>
            <Typography variant="h6" fontWeight={700}>
              Topic map
            </Typography>
            <Stack
              direction="row"
              spacing={0.75}
              alignItems="center"
              sx={{ color: "text.secondary" }}
            >
              <PanToolAltRoundedIcon sx={{ fontSize: 14 }} />
              <Typography variant="caption">
                {chart?.source === "ai" ? "AI-generated" : "Heuristic"} graph ·
                drag cards to rearrange, click for details
              </Typography>
            </Stack>
          </Box>
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          {/* Zoom cluster — out / fit-to-screen / in / percentage.
              Sized to feel like a tight pill group so it reads as
              one control rather than three independent buttons. */}
          <Stack
            direction="row"
            spacing={0.5}
            alignItems="center"
            sx={(t) => ({
              border: `1px solid ${t.palette.divider}`,
              borderRadius: 2,
              p: 0.4,
              backgroundColor:
                t.palette.mode === "dark"
                  ? alpha(t.palette.background.paper, 0.6)
                  : t.palette.background.paper,
            })}
          >
            <Tooltip arrow title="Zoom out">
              <span>
                <MDButton
                  variant="text"
                  color="dark"
                  size="small"
                  iconOnly
                  onClick={zoomOut}
                  disabled={zoom <= MIN_ZOOM + 0.001}
                  sx={{ minWidth: 28, px: 0.5 }}
                >
                  <RemoveRoundedIcon fontSize="small" />
                </MDButton>
              </span>
            </Tooltip>
            <Tooltip arrow title="Fit to view">
              <span>
                <MDButton
                  variant="text"
                  color="dark"
                  size="small"
                  onClick={zoomFit}
                  sx={{ minWidth: 56, fontWeight: 700, fontSize: 12 }}
                >
                  <CenterFocusStrongRoundedIcon
                    fontSize="small"
                    sx={{ mr: 0.5 }}
                  />
                  {Math.round(zoom * 100)}%
                </MDButton>
              </span>
            </Tooltip>
            <Tooltip arrow title="Zoom in">
              <span>
                <MDButton
                  variant="text"
                  color="dark"
                  size="small"
                  iconOnly
                  onClick={zoomIn}
                  disabled={zoom >= MAX_ZOOM - 0.001}
                  sx={{ minWidth: 28, px: 0.5 }}
                >
                  <AddRoundedIcon fontSize="small" />
                </MDButton>
              </span>
            </Tooltip>
          </Stack>
          {hasPositionOverrides ? (
            <Tooltip arrow title="Snap cards back to the auto tree layout">
              <span>
                <MDButton
                  variant="outlined"
                  color="dark"
                  size="small"
                  startIcon={<RestartAltRoundedIcon fontSize="small" />}
                  onClick={resetLayout}
                >
                  Reset layout
                </MDButton>
              </span>
            </Tooltip>
          ) : null}
          <MDButton
            variant="gradient"
            color="warning"
            disabled={deepening}
            onClick={deepenChart}
            startIcon={
              deepening ? (
                <CircularProgress size={14} color="inherit" />
              ) : (
                <RocketLaunchRoundedIcon />
              )
            }
          >
            Deepen with AI (1 credit)
          </MDButton>
        </Stack>
      </Stack>

      {!chart || !Array.isArray(chart.nodes) || chart.nodes.length === 0 ? (
        <Paper
          variant="outlined"
          sx={{
            p: 4,
            textAlign: "center",
            borderRadius: 3,
            borderStyle: "dashed",
          }}
        >
          <AutoAwesomeRoundedIcon
            sx={{ fontSize: 36, color: "text.disabled", mb: 1 }}
          />
          <Typography variant="body2" color="text.secondary">
            No topics graph yet. Tap "Deepen with AI" above to generate one.
          </Typography>
        </Paper>
      ) : (
        <Paper
          variant="outlined"
          sx={(t) => {
            const isDark = t.palette.mode === "dark";
            return {
              borderRadius: 3,
              borderColor: t.palette.divider,
              backgroundImage: isDark
                ? `radial-gradient(circle at 50% 0%, ${alpha(
                    t.palette.primary.main,
                    0.16,
                  )} 0%, ${alpha(t.palette.background.paper, 0)} 60%)`
                : `radial-gradient(circle at 50% 0%, ${alpha(
                    t.palette.primary.main,
                    0.07,
                  )} 0%, transparent 60%)`,
              backgroundColor: isDark
                ? alpha(t.palette.background.paper, 0.5)
                : alpha(t.palette.background.paper, 0.9),
              overflow: "hidden",
            };
          }}
        >
          {/*
            Scroll container. Caps vertical height at 720 px so
            the topic tree doesn't hijack the entire viewport on
            huge graphs — beyond that it scrolls. Horizontal
            overflow is allowed so wide trees pan sideways
            instead of forcing the cards to overlap.

            The outer Box owns scroll. Inside it sits a SIZING
            box whose dimensions are the layout × zoom (so the
            scroll thumbs reflect the visible-content size), and
            inside THAT sits the actual CANVAS (unscaled layout
            box, transformed via CSS scale). Cards + SVG are
            authored in unscaled layout coords throughout — only
            the canvas Box knows about zoom.
          */}
          <Box
            ref={scrollWrapRef}
            sx={{
              maxHeight: 720,
              overflow: "auto",
              "&::-webkit-scrollbar": {
                width: 10,
                height: 10,
              },
              "&::-webkit-scrollbar-thumb": {
                borderRadius: 5,
                backgroundColor: alpha(theme.palette.text.secondary, 0.25),
              },
              "&::-webkit-scrollbar-thumb:hover": {
                backgroundColor: alpha(theme.palette.text.secondary, 0.4),
              },
            }}
          >
            <Box
              sx={{
                position: "relative",
                width: layout.bounds.w * zoom,
                height: layout.bounds.h * zoom,
              }}
            >
              <Box
                ref={canvasRef}
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: layout.bounds.w,
                  height: layout.bounds.h,
                  transform: `scale(${zoom})`,
                  transformOrigin: "0 0",
                  userSelect: "none",
                  WebkitUserSelect: "none",
                  touchAction: "none",
                }}
              >
                {/* Edge layer */}
                <Box
                  component="svg"
                  viewBox={`0 0 ${layout.bounds.w} ${layout.bounds.h}`}
                  sx={{
                    position: "absolute",
                    inset: 0,
                    width: "100%",
                    height: "100%",
                    pointerEvents: "none",
                    display: "block",
                  }}
                >
                  <defs>
                    <EdgeArrowMarker id="topic-tree-arrow" />
                  </defs>
                  {layout.edges.map((e, i) => {
                    const from = layout.nodes.find(
                      (n) => n.id === (e.source || e.from),
                    );
                    const to = layout.nodes.find(
                      (n) => n.id === (e.target || e.to),
                    );
                    if (!from || !to) return null;
                    const fromPos = resolvePos(from);
                    const toPos = resolvePos(to);
                    const isConnectedToHover =
                      hoverNodeId &&
                      (hoverNodeId === from.id || hoverNodeId === to.id);
                    return (
                      <TreeEdge
                        key={`e-${i}`}
                        fromPos={fromPos}
                        toPos={toPos}
                        cardH={layout.cardH}
                        emphasized={isConnectedToHover}
                      />
                    );
                  })}
                </Box>

                {/* Card layer */}
                {layout.nodes.map((n) => {
                  const pos = resolvePos(n);
                  const isHover = hoverNodeId === n.id;
                  const isThisDragging = dragging?.nodeId === n.id;
                  return (
                    <TopicCard
                      key={n.id}
                      node={n}
                      cx={pos.x}
                      cy={pos.y}
                      cardW={layout.cardW}
                      cardH={layout.cardH}
                      emphasized={isHover || isThisDragging}
                      onPointerEnter={() => setHoverNodeId(n.id)}
                      onPointerLeave={() =>
                        setHoverNodeId((id) => (id === n.id ? null : id))
                      }
                      onPointerDown={(e) => handleCardPointerDown(e, n)}
                    />
                  );
                })}
              </Box>
            </Box>
          </Box>
        </Paper>
      )}

      <Dialog
        open={!!drillNode}
        onClose={() => setDrillNode(null)}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        {drillNode ? (
          <>
            <DialogTitle>
              <Typography variant="h6" fontWeight={700}>
                {drillNode.label || drillNode.title || "Topic"}
              </Typography>
            </DialogTitle>
            <DialogContent>
              {drillNode.summary || drillNode.description ? (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 1.5 }}
                >
                  {drillNode.summary || drillNode.description}
                </Typography>
              ) : null}
              <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ rowGap: 1 }}>
                {drillNode.quizCount ? (
                  <Chip
                    icon={<QuizRoundedIcon fontSize="small" />}
                    label={`${drillNode.quizCount} quiz question${
                      drillNode.quizCount === 1 ? "" : "s"
                    }`}
                    color="info"
                    variant="outlined"
                  />
                ) : null}
                {drillNode.flashcardCount ? (
                  <Chip
                    icon={<StyleRoundedIcon fontSize="small" />}
                    label={`${drillNode.flashcardCount} flashcard${
                      drillNode.flashcardCount === 1 ? "" : "s"
                    }`}
                    color="success"
                    variant="outlined"
                  />
                ) : null}
                {drillCoverage === 0 ? (
                  <Chip
                    label="No coverage yet — generate quizzes / flashcards from the Chat tab"
                    color="warning"
                    variant="outlined"
                  />
                ) : null}
              </Stack>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2.5 }}>
              {drillNode.quizCount ? (
                <MDButton
                  variant="outlined"
                  color="info"
                  startIcon={<QuizRoundedIcon fontSize="small" />}
                  onClick={() => {
                    onNavigateToTab?.("quiz");
                    setDrillNode(null);
                  }}
                >
                  Practise quiz
                </MDButton>
              ) : null}
              {drillNode.flashcardCount ? (
                <MDButton
                  variant="outlined"
                  color="success"
                  startIcon={<StyleRoundedIcon fontSize="small" />}
                  onClick={() => {
                    onNavigateToTab?.("flashcards");
                    setDrillNode(null);
                  }}
                >
                  Review flashcards
                </MDButton>
              ) : null}
              <MDButton
                variant="text"
                color="dark"
                onClick={() => setDrillNode(null)}
              >
                Close
              </MDButton>
            </DialogActions>
          </>
        ) : null}
      </Dialog>
    </Stack>
  );
}

/**
 * A single topic card. HTML element (not SVG) so we get full
 * MUI styling: gradient background, drop shadow, icon, two
 * lines of text, coverage stats. Positioned absolutely with
 * the centre at (cx, cy); cards render on top of the SVG edge
 * layer.
 *
 * Drag is initiated on pointerdown — the window-level
 * pointermove + pointerup listeners installed by the parent
 * effect take over from there.
 */
function TopicCard({
  node,
  cx,
  cy,
  cardW,
  cardH,
  emphasized,
  onPointerEnter,
  onPointerLeave,
  onPointerDown,
}) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const palette = cardPalette(node, theme);
  const label = node.label || node.title || "Topic";
  const coverage = (node.quizCount || 0) + (node.flashcardCount || 0);
  const cat = categoryOf(node);
  const isPainted = cat === "root" || cat === "high" || cat === "mid";

  return (
    <Box
      onPointerEnter={onPointerEnter}
      onPointerLeave={onPointerLeave}
      onPointerDown={onPointerDown}
      sx={{
        position: "absolute",
        top: cy,
        left: cx,
        transform: `translate(-50%, -50%) ${emphasized ? "scale(1.04)" : "scale(1)"}`,
        width: cardW,
        minHeight: cardH,
        cursor: "grab",
        "&:active": { cursor: "grabbing" },
        background: palette.gradient,
        border: "1.5px solid",
        borderColor: palette.border,
        borderRadius: 2.5,
        px: 1.25,
        py: 0.9,
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        gap: 0.4,
        boxShadow: emphasized
          ? isDark
            ? `0 12px 28px ${alpha("#000", 0.55)}, 0 0 0 3px ${alpha(
                palette.border,
                0.35,
              )}`
            : `0 14px 28px ${alpha(palette.border, 0.35)}, 0 0 0 3px ${alpha(
                palette.border,
                0.18,
              )}`
          : isDark
            ? `0 6px 14px ${alpha("#000", 0.45)}`
            : `0 6px 14px ${alpha("#0f172a", 0.12)}`,
        transition:
          "transform .15s ease, box-shadow .2s ease, border-color .2s ease",
        // Don't let the browser's drag-image kick in over the
        // card title; we're handling drag with pointer events.
        WebkitUserDrag: "none",
        userDrag: "none",
        userSelect: "none",
      }}
    >
      <Stack direction="row" spacing={0.6} alignItems="center">
        {node.isRoot ? (
          <HubRoundedIcon
            sx={{
              fontSize: 16,
              color: palette.text,
              opacity: 0.95,
              flexShrink: 0,
            }}
          />
        ) : (
          <FiberManualRecordRoundedIcon
            sx={{
              fontSize: 9,
              color: isPainted ? alpha("#fff", 0.85) : palette.border,
              flexShrink: 0,
            }}
          />
        )}
        <Typography
          sx={{
            color: palette.text,
            fontWeight: 700,
            fontSize: 13,
            lineHeight: 1.2,
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {label}
        </Typography>
      </Stack>
      {coverage > 0 ? (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ pl: 1.6 }}>
          {node.quizCount ? (
            <Stack direction="row" spacing={0.3} alignItems="center">
              <QuizRoundedIcon sx={{ fontSize: 11, color: palette.sub }} />
              <Typography
                sx={{ color: palette.sub, fontSize: 10.5, fontWeight: 600 }}
              >
                {node.quizCount}
              </Typography>
            </Stack>
          ) : null}
          {node.flashcardCount ? (
            <Stack direction="row" spacing={0.3} alignItems="center">
              <StyleRoundedIcon sx={{ fontSize: 11, color: palette.sub }} />
              <Typography
                sx={{ color: palette.sub, fontSize: 10.5, fontWeight: 600 }}
              >
                {node.flashcardCount}
              </Typography>
            </Stack>
          ) : null}
        </Stack>
      ) : (
        <Typography
          sx={{
            color: palette.sub,
            fontSize: 10,
            fontWeight: 500,
            pl: 1.6,
            fontStyle: "italic",
          }}
        >
          no coverage yet
        </Typography>
      )}
    </Box>
  );
}

/**
 * A top-down tree edge between two cards. Drawn as a sigmoid
 * cubic-bezier from the bottom centre of the parent card to
 * the top centre of the child card so it reads as "flow goes
 * downward". The two handles sit at the vertical midpoint
 * which gives the path its characteristic S-curve.
 *
 * Edges connected to a hovered node get bumped opacity +
 * stroke width so the user can trace neighbours visually.
 */
function TreeEdge({ fromPos, toPos, cardH, emphasized }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  // Start at bottom of parent card, end at top of child card.
  const x1 = fromPos.x;
  const y1 = fromPos.y + cardH / 2;
  const x2 = toPos.x;
  const y2 = toPos.y - cardH / 2;
  // Bezier handles sit at the vertical midpoint between the
  // two endpoints. This produces a smooth S-curve regardless
  // of whether the parent is above-left or above-right of the
  // child, and naturally degenerates to a near-vertical line
  // when parent + child share an x coordinate.
  const midY = (y1 + y2) / 2;
  const d = `M ${x1} ${y1} C ${x1} ${midY}, ${x2} ${midY}, ${x2} ${y2}`;
  const strokeColor = isDark
    ? alpha(theme.palette.primary.light, emphasized ? 0.95 : 0.5)
    : alpha(theme.palette.primary.main, emphasized ? 0.85 : 0.55);
  return (
    <path
      d={d}
      markerEnd="url(#topic-tree-arrow)"
      fill="none"
      stroke={strokeColor}
      strokeWidth={emphasized ? 3 : 2}
      strokeLinecap="round"
      style={{
        // `color` cascades into the marker via currentColor so
        // the arrow head paints in the same shade as the
        // stroke — they always stay in sync.
        color: strokeColor,
        transition: "stroke-width .15s ease, stroke .15s ease",
      }}
    />
  );
}

function EdgeArrowMarker({ id }) {
  return (
    <marker
      id={id}
      viewBox="0 0 12 12"
      refX="10"
      refY="6"
      markerWidth="8"
      markerHeight="8"
      orient="auto-start-reverse"
    >
      <path d="M 0 0 L 12 6 L 0 12 z" fill="currentColor" />
    </marker>
  );
}
